import React from "react";
import { Dialog, IconButton } from "@mui/material";
import { Close, ContentCopyRounded } from "@mui/icons-material";
import { useTheme } from "../../ThemeContext";

const ProjectSummaryDialog = ({
  open,
  onClose,
  summaryPlainText,
  summaryHtmlText,
  summaryFormat = "html",
  summaryCurrency = "GBP",
  onFormatChange,
  onCurrencyChange,
  projectRef,
  onCopy,
  hasCopied,
  isLoading = false,
  stageText = "",
}) => {
  const { theme } = useTheme();
  const isHtml = summaryFormat === "html";
  const plainValue = summaryPlainText || "No summary available.";
  const htmlValue = summaryHtmlText || "<p>No summary available.</p>";
  const currencyOptions = [
    { key: "GBP", label: "GBP" },
    { key: "AED", label: "AED" },
    { key: "GBP_AED", label: "GBP & AED" },
  ];
  const activeCurrency = (summaryCurrency || "GBP").toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
        },
      }}
    >
      <div
        className={`${theme.formHeaderBg} px-6 py-4 flex items-center justify-between`}
      >
        <div>
          <h2 className="text-white font-bold text-lg">Project Summary</h2>
          <p className={`text-sm ${theme.formSubText}`}>
            {projectRef
              ? `Reference: ${projectRef}`
              : "Ready to copy for email"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={isLoading}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-white transition ${
              isLoading
                ? "bg-white/20 cursor-not-allowed opacity-70"
                : "bg-white/10 hover:bg-white/20"
            }`}
          >
            <ContentCopyRounded sx={{ fontSize: 18 }} />
            <span className="text-sm font-medium">
              {hasCopied ? "Copied" : "Copy"}
            </span>
          </button>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            className="hover:bg-white/10"
          >
            <Close />
          </IconButton>
        </div>
      </div>

      <div
        className="px-5 pt-5 pb-6 bg-gray-50 border-t border-gray-100"
        style={{ maxHeight: "calc(92vh - 72px)", overflowY: "auto" }}
      >
        <div className="flex items-center gap-2 mb-3">
          {currencyOptions.map(({ key, label }) => {
            const isActive = activeCurrency === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onCurrencyChange && onCurrencyChange(key)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  isActive
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {label}
              </button>
            );
          })}
          <div className="h-6 border-l border-gray-200 mx-2" />
          <button
            type="button"
            onClick={() => onFormatChange && onFormatChange("html")}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              isHtml
                ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            HTML (email)
          </button>
          <button
            type="button"
            onClick={() => onFormatChange && onFormatChange("plain")}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              !isHtml
                ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Plain text
          </button>
        </div>

        {isLoading ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl text-gray-700 flex items-center justify-center p-6 min-h-[320px]">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <div className="text-sm font-semibold">
                {stageText || "Generating summary..."}
              </div>
              <div className="text-xs text-gray-500 text-center">
                Preparing an email-ready summary. Please wait a moment.
              </div>
            </div>
          </div>
        ) : isHtml ? (
          <>
            <label className="text-sm font-medium text-gray-700">Preview</label>
            <div
              className="mt-2 p-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 leading-6 overflow-auto"
              style={{ maxHeight: "60vh", minHeight: "320px" }}
              dangerouslySetInnerHTML={{ __html: htmlValue }}
            />
          </>
        ) : (
          <>
            <label className="text-sm font-medium text-gray-700">
              Plain text, ready to paste
            </label>
            <textarea
              readOnly
              value={plainValue}
              className="mt-2 w-full h-72 p-3 rounded-xl border border-gray-200 bg-white font-mono text-sm text-gray-800 leading-6 focus:outline-none"
              aria-label="Project summary ready to copy"
            />
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ProjectSummaryDialog;
